import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

// Utils
import { getElementId, getTableContentColWidthsGU } from '../../../common/elements/utils/elementPropertyUtils';
import { asObject } from '../../../common/utils/immutableHelper';
import { getHotTableWidthById } from './utils/tableDOMUtils';
import { convertColWidthsGUtoPx, getBorderRenderedValues } from './utils/tableSizeUtils';

// styles
import './PresentationalTable.scss';

// Constants
import { IN_LIST_MODE } from '../../../common/columns/columnConstants';
/**
 * Clone the dom if presentational to avoid re-rendering the table
 */

export const PresentationalTable = ({ element, gridSize, inList, inTrash, isDragging }) => {
    const elementId = getElementId(element);
    const presentationalTableRef = useRef(null);

    // Select the part of the table that we want to display when dragging, ignoring things like ref headers
    const currentTable = document.querySelector(`#el-${elementId} .HotTable .HotTableLib .ht_master`);
    const scrollPosition = currentTable.querySelector('.wtHolder').scrollLeft;
    const clonedTable = currentTable?.cloneNode(true);

    const updatePresentationalColWidths = () => {
        // Re-adjust column widths to match data. This is required because column widths can be
        // adjusted slightly to fit columns.

        // Inside trash and in mobile view are the only time we use the presentational table without dragging
        // in this case we want to resize it to the width of the container
        const shouldResizeToWidth = (inTrash || inList === IN_LIST_MODE.IN_LIST_DOCUMENT_MODE) && !isDragging;
        const resizeWidth = shouldResizeToWidth ? getHotTableWidthById(elementId) : null;

        const colWidthsGU = asObject(getTableContentColWidthsGU(element));
        const colWidthsPx = convertColWidthsGUtoPx(colWidthsGU, gridSize, resizeWidth);
        const colGroup = clonedTable.querySelector('table.htCore colgroup');

        for (let i = 1; i < colGroup.children.length; i++) {
            colGroup.children[i].style.width = `${colWidthsPx[i - 1]}px`;
        }
    };

    useEffect(() => {
        if (!clonedTable || !presentationalTableRef.current) return;

        // Calculate border render values
        const { actualBorderWidth, missingBorderWidth } = getBorderRenderedValues(currentTable.querySelector('td'));
        presentationalTableRef.current.style.setProperty('--1px-border-rendered-width', `${actualBorderWidth}px`);
        presentationalTableRef.current.style.setProperty('--1px-border-missing-width', `${missingBorderWidth}px`);

        const isMobileView = inList === IN_LIST_MODE.IN_LIST_DOCUMENT_MODE;
        // This just excludes the scenario when we are dragging a column with a table inside
        // in which case we don't want to recalculate the widths
        const columnIsDragging = inList && !isMobileView && !inTrash;
        if (!columnIsDragging) updatePresentationalColWidths();

        // if there is already table content, remove it
        if (presentationalTableRef.current.firstChild) {
            presentationalTableRef.current.removeChild(presentationalTableRef.current.firstChild);
        }
        presentationalTableRef.current.appendChild(clonedTable);

        if ((columnIsDragging || isMobileView) && scrollPosition) {
            // if dragging in column, make sure the scroll position matches
            presentationalTableRef.current.querySelector('.wtHolder').scrollLeft = scrollPosition;
        }
        // remove unnecessary elements
        presentationalTableRef.current.querySelector('.htBorders')?.remove();
        presentationalTableRef.current.querySelector('thead')?.remove();

        // Remove parts of Milanote cell renderer that cause lots of hit tests
        presentationalTableRef.current.querySelectorAll('.MilanoteCellRendererComponent').forEach((renderer) => {
            const draftEditorRoot = renderer.querySelector('.DraftEditor-root');
            if (!draftEditorRoot) return;

            // Remove all the classnames that cause hit tests
            renderer.querySelector('.DraftEditor-editorContainer').className = '';
            renderer.querySelector('.public-DraftEditor-content').className = '';
            renderer.querySelector('.public-DraftStyleDefault-block').className = '';

            // Keep the alignment classname on draft editor root
            const alignmentClass = draftEditorRoot.className.split(' ')[1];
            draftEditorRoot.className = `presentation-cell-content ${alignmentClass}`;
        });
    }, [isDragging, inTrash]);

    return (
        <div className="HotTable presentational-table">
            <div className="HotTableLib handsontable" ref={presentationalTableRef}></div>
        </div>
    );
};

PresentationalTable.propTypes = {
    element: PropTypes.object,
    gridSize: PropTypes.number,
    inList: PropTypes.string,
    inTrash: PropTypes.bool,
    isDragging: PropTypes.bool,
};

export default PresentationalTable;
